import React, { useEffect, useState, useCallback, useRef } from "react";
import { AppBar, Box, Badge, IconButton, Toolbar, Typography, Avatar, Button, Dialog, DialogTitle, DialogContent, Chip, Divider, CircularProgress, Menu, MenuItem, ListItemText, ListItemAvatar, useMediaQuery, useTheme } from "@mui/material";
// [PROFILE PIC UPLOAD] PhotoCameraIcon used for the upload button overlay on the avatar
import { Logout, Login, Menu as MenuIcon, Notifications as NotificationsIcon, CalendarToday as CalendarTodayIcon, Logout as LogoutIcon, Close as CloseIcon, BusinessCenter as BusinessCenterIcon, /* PhotoCamera as PhotoCameraIcon, */ EventAvailable as EventAvailableIcon, CheckCircle } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getData, postData } from "../services/axiosInstance";
import { API_ENDPOINTS } from "../services/endpoints";
import MyProfilePage from "../features/profile/myProfilePage";
import { formatDate } from "../utils/dateFormat";
import MyAttendancePunch from "./common/myAttendancePunch";
import { getUserRole } from "../utils/auth";
import { getNotificationPath } from "../utils/notificationNavigation";
// [PROFILE PIC UPLOAD] axiosInstance used directly to send multipart/form-data for profile picture upload
// import { axiosInstance } from "../services/axiosInstance";

const Navbar = ({ onMenuClick }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isExtraSmall = useMediaQuery("(max-width:425px)");
    const navigate = useNavigate();
    const [userData] = useState(() => JSON.parse(localStorage.getItem("userData") || "{}"));
    const [userDetails, setUserDetails] = useState(null);
    const [openProfile, setOpenProfile] = useState(false);
    const [isCheckedIn, setIsCheckedIn] = useState(false);
    const [notificationCount, setNotificationCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [loadingNotifications, setLoadingNotifications] = useState(false);
    // [PROFILE PIC UPLOAD] local preview URL shown while upload is in progress
    // const [profilePreview, setProfilePreview] = useState(null);
    const [openCheckInDialog, setOpenCheckInDialog] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const userRole = getUserRole()?.toLowerCase();

    // [PROFILE PIC UPLOAD] ref to the hidden file input element
    // const fileInputRef = useRef(null);
    const userId = userData?.id;

    const status = userDetails?.status?.toLowerCase() || 'inactive';
    const isActive = status === 'active';
    const openNotifications = Boolean(anchorEl);

    const fetchUserDetails = useCallback(async () => {
        if (!userId) return;
        try {
            const response = await getData(`${API_ENDPOINTS.fetchUserDetails}?userId=${userId}`);
            if (response.status === "SUCCESS") {
                setUserDetails(response.message.user);
            }
        } catch (error) { return null; }
    }, [userId]);

    const fetchNotificationCount = useCallback(async () => {
        if (!userId) return;
        try {
            const response = await getData(API_ENDPOINTS.fetchNotificationCount(userId));
            if (response.status === "SUCCESS") {
                setNotificationCount(response.message.count);
            }
        } catch (error) { return null; }
    }, [userId]);

    const fetchNotificationsList = useCallback(async () => {
        if (!userId) return;
        setLoadingNotifications(true);
        try {
            const response = await getData(API_ENDPOINTS.fetchUserNotifications(userId));
            if (response.status === "SUCCESS") {
                setNotifications(response.message);
            }
        } catch (error) {
            return null;
        } finally {
            setLoadingNotifications(false);
        }
    }, [userId]);

    const checkShiftTiming = useCallback((record) => {
        const now = new Date();
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();

        const isPastShiftEnd = currentHour > 19 || (currentHour === 19 && currentMinute >= 30);

        if (record) {
            const hasCheckedIn = !!record.check_in;
            const hasCheckedOut = !!record.check_out;
            if (hasCheckedOut || (hasCheckedIn && isPastShiftEnd)) {
                setIsCompleted(true);
                setIsCheckedIn(false);
            } else if (hasCheckedIn) {
                setIsCheckedIn(true);
                setIsCompleted(false);
            } else {
                setIsCheckedIn(false);
                setIsCompleted(false);
            }
        } else {
            if (isPastShiftEnd) {
                setIsCompleted(true);
            }
        }
    }, []);

    useEffect(() => {
        fetchUserDetails();
        fetchNotificationCount();

        const handleUpdateEvent = () => fetchNotificationCount();
        window.addEventListener("notificationsUpdated", handleUpdateEvent);

        const intervalId = setInterval(fetchNotificationCount, 60 * 1000);

        return () => {
            window.removeEventListener("notificationsUpdated", handleUpdateEvent);
            clearInterval(intervalId);
        };
    }, [fetchUserDetails, fetchNotificationCount]);

    useEffect(() => {
        const checkCurrentStatus = async () => {
            if (!userId) return;
            try {
                const response = await getData(API_ENDPOINTS.getTodayAttendanceRecord(userId));
                if (response.status === "SUCCESS" && response.result) {
                    checkShiftTiming(response.result);
                } else {
                    checkShiftTiming(null);
                }
            } catch (error) {
                checkShiftTiming(null);
            }
        };

        checkCurrentStatus();
        const timer = setInterval(checkCurrentStatus, 60000);
        return () => clearInterval(timer);
    }, [userId, checkShiftTiming]);

    const handleNotificationClick = (event) => {
        setAnchorEl(event.currentTarget);
        fetchNotificationsList();
    };

    const handleNotificationClose = () => setAnchorEl(null);
    const handleCheckToggle = () => {
        setOpenCheckInDialog(true);
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate("/login");
    };

    const getInitials = (name) => {
        if (!name || name === "undefined undefined") return "UP";
        return name.split(" ").filter(Boolean).map(n => n[0]).join("").toUpperCase().substring(0, 2);
    };

    const formatNotificationDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).replace(/\s/g, '')}`;
    };

    const handleMarkAsRead = async (notifId) => {
        try {
            const response = await postData(API_ENDPOINTS.markNotificationsAsRead, {
                notification_ids: [notifId]
            });
            if (response.status === "SUCCESS") {
                fetchNotificationsList();
                fetchNotificationCount();
            }
        } catch (error) {
            return null;
        }
    };

    const handleNotificationItemClick = (notif) => {
        handleMarkAsRead(notif.id);
        const path = getNotificationPath(notif, userData?.user_type);
        if (path) {
            handleNotificationClose();
            navigate(path);
        }
    };

    // [PROFILE PIC UPLOAD] opens the hidden file input when the camera icon is clicked
    // const handleEditClick = () => { fileInputRef.current?.click(); };

    // [PROFILE PIC UPLOAD] validates the selected image, shows a local preview, uploads to backend
    // (POST /api/organization/uploadProfilePic) via multipart/form-data, then updates the preview
    // with the returned Azure Blob URL; clears preview on failure
    // const handleFileChange = async (event) => {
    //     const file = event.target.files[0];
    //     if (!file) return;
    //     const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    //     if (!validTypes.includes(file.type)) {
    //         alert("Please select a valid image (PNG, JPG, or JPEG)");
    //         return;
    //     }
    //     const previewUrl = URL.createObjectURL(file);
    //     setProfilePreview(previewUrl);
    //     try {
    //         const formData = new FormData();
    //         formData.append('user_pic', file);
    //         const response = await axiosInstance.post(
    //             API_ENDPOINTS.uploadProfilePic(userId),
    //             formData
    //         );
    //         if (response.data?.status === 'SUCCESS') {
    //             const picUrl = response.data?.message?.user_pic;
    //             if (picUrl) setProfilePreview(picUrl);
    //         } else {
    //             setProfilePreview(null);
    //         }
    //     } catch {
    //         setProfilePreview(null);
    //     }
    //     event.target.value = '';
    // };

    useEffect(() => {
        const checkCurrentStatus = async () => {
            if (!userId) return;
            try {
                const response = await getData(API_ENDPOINTS.getTodayAttendanceRecord(userId));
                if (response.status === "SUCCESS" && response.result) {
                    const record = response.result;
                    setIsCheckedIn(!!(record.check_in && !record.check_out));
                    setIsCompleted(!!(record.check_in && record.check_out));
                }
            } catch (error) { return null; }
        };
        checkCurrentStatus();
    }, [userId]);

    return (
        <AppBar
            position="fixed"
            elevation={0}
            sx={{
                backgroundColor: "#2c3e50",
                width: { md: `calc(100% - 260px)` },
                height: '70px',
                ml: { md: `260px` }
            }}
        >
            <Toolbar sx={{ justifyContent: "space-between", height: '100%', px: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                    <IconButton color="inherit" onClick={onMenuClick} sx={{ mr: { xs: 0.5, sm: 2 }, display: { md: 'none' } }}>
                        <MenuIcon />
                    </IconButton>
                    <Box sx={{ overflow: 'hidden' }}>
                        <Typography
                            variant="subtitle2"
                            component="div"
                            sx={{
                                fontSize: { xs: 14, sm: 18 },
                                fontWeight: 600,
                                textTransform: 'capitalize',
                                lineHeight: 1.2,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                        >
                            {isExtraSmall
                                ? userDetails?.first_name
                                : `Welcome, ${userDetails?.first_name || 'User'} ${userDetails?.last_name || ''}`.trim()
                            }
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', opacity: 0.8, mt: 0.5 }}>
                            <CalendarTodayIcon sx={{ fontSize: 14, mr: 0.5 }} />
                            <Typography variant="caption" sx={{ fontSize: 10, mb: 0 }}>
                                {new Date().toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: 'long',
                                    year: 'numeric'
                                })}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 } }}>
                    {isCompleted ? (
                        <Chip
                            icon={<CheckCircle style={{ color: '#fff' }} />}
                            label="Shift Ended"
                            sx={{ bgcolor: '#2e7d32', color: '#fff', fontWeight: 'bold' }}
                        />
                    ) : (
                        /* STATE 1 & 2: IN / OUT */
                        <Button
                            variant={isCheckedIn ? "outlined" : "contained"}
                            color={isCheckedIn ? "error" : "primary"}
                            onClick={handleCheckToggle}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 'bold',
                                minWidth: isExtraSmall ? '40px' : 'auto',
                                px: isExtraSmall ? 1 : 2,
                                display: userRole != 'approver' ? 'block' : 'none'
                            }}
                        >
                            {isExtraSmall ? (
                                isCheckedIn ? <Logout fontSize="small" /> : <Login fontSize="small" />
                            ) : (
                                isCheckedIn ? "Check-Out" : "Check-In"
                            )}
                        </Button>
                    )}

                    <IconButton color="inherit" onClick={handleNotificationClick} size={isExtraSmall ? "small" : "medium"}>
                        <Badge badgeContent={notificationCount} color="error" overlap="circular">
                            <NotificationsIcon fontSize={isExtraSmall ? "small" : "medium"} />
                        </Badge>
                    </IconButton>

                    <Avatar
                        sx={{ width: { xs: 32, sm: 38 }, height: { xs: 32, sm: 38 }, bgcolor: '#0d47a1', cursor: 'pointer', border: '1px solid #fff', fontSize: { xs: '12px', sm: '16px' }, textTransform: 'uppercase' }}
                        onClick={() => setOpenProfile(true)}
                    >
                        {userDetails ? `${userDetails.first_name?.charAt(0)}${userDetails.last_name?.charAt(0)}` : 'UP'}
                    </Avatar>
                </Box>
            </Toolbar>

            {/* Notifications Menu */}
            <Menu
                anchorEl={anchorEl}
                open={openNotifications}
                onClose={handleNotificationClose}
                disableScrollLock
                PaperProps={{
                    sx: {
                        mt: 1.5,
                        width: { xs: '95vw', sm: 380 },
                        maxWidth: '100%',
                        maxHeight: 500,
                        borderRadius: 2,
                        boxShadow: '0px 10px 25px rgba(0,0,0,0.15)',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8f9fa' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Notifications</Typography>
                    <IconButton size="small" onClick={handleNotificationClose}><CloseIcon fontSize="small" /></IconButton>
                </Box>
                <Divider />
                <Box sx={{ flexGrow: 1, overflowY: 'auto', maxHeight: '350px' }}>
                    {loadingNotifications ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24} /></Box>
                    ) : notifications.length > 0 ? (
                        notifications.map((notif) => (
                            <MenuItem key={notif.id} onClick={() => handleNotificationItemClick(notif)} sx={{ whiteSpace: 'normal', py: 2, borderBottom: '1px solid #f0f0f0', bgcolor: notif.is_read ? 'transparent' : 'rgba(25, 118, 210, 0.06)' }}>
                                <ListItemAvatar>
                                    <Badge overlap="circular" variant="dot" invisible={notif.is_read} color="primary">
                                        <Avatar sx={{ bgcolor: '#34495e', width: 40, height: 40, fontSize: '0.9rem' }}>{getInitials(notif.sender_type)}</Avatar>
                                    </Badge>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ fontWeight: notif.is_read ? 500 : 800 }}>{notif.sender_type || "Update"}</Typography>
                                            <Typography variant="caption" color="text.secondary">{formatNotificationDate(notif.createdAt)}</Typography>
                                        </Box>
                                    }
                                    secondary={<Typography variant="body2" sx={{ mt: 0.5, color: notif.is_read ? 'text.secondary' : 'text.primary' }}>{notif.message}</Typography>}
                                />
                            </MenuItem>
                        ))
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <NotificationsIcon sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">You're all caught up!</Typography>
                        </Box>
                    )}
                </Box>
                <Divider />
                <Box sx={{ p: 1 }}>
                    <Button fullWidth color="primary" sx={{ textTransform: 'none' }} onClick={() => {
                        handleNotificationClose();
                        const role = userData?.user_type?.toLowerCase();
                        navigate(
                            role === "manager" ? "/manager/notifications" :
                            role === "hr" ? "/hr/notifications" :
                            role === "team lead" ? "/team-lead/notifications" :
                            role === "approver" ? "/approver/notifications" :
                            "/employee/notifications"
                        );
                    }}>View All</Button>
                </Box>
            </Menu>

            {/* Profile Dialog */}
            <Dialog open={openProfile} onClose={() => setOpenProfile(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: { xs: 2, sm: 4 }, margin: { xs: 1, sm: 2 } } }}>
                <DialogTitle sx={{ bgcolor: '#2c3e50', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: { xs: 1.5, sm: 2 } }}>
                    {/* Fixed nested typography hydration error */}
                    <Typography variant="h6" component="span" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Profile Details
                    </Typography>
                    <IconButton size="small" color="inherit" onClick={() => setOpenProfile(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: { xs: 2, sm: 3 }, bgcolor: '#f8f9fa', textAlign: { xs: 'center', sm: 'left' } }}>
                        {/* [PROFILE PIC UPLOAD] hidden file input — triggers on camera icon click */}
                        {/* <input type="file" ref={fileInputRef} hidden accept="image/*" onChange={handleFileChange} /> */}
                        <Box sx={{ position: 'relative' }}>
                            {/* [PROFILE PIC UPLOAD] Avatar shows local preview or stored user_pic URL; falls back to initials */}
                            {/* <Avatar src={profilePreview || userDetails?.user_pic} sx={{ width: { xs: 80, sm: 100 }, height: { xs: 80, sm: 100 }, bgcolor: '#34495e', border: '4px solid #fff', boxShadow: 2 }}> */}
                            {/*     {!profilePreview && !userDetails?.user_pic && getInitials(`${userDetails?.first_name} ${userDetails?.last_name}`)} */}
                            {/* </Avatar> */}
                            <Avatar sx={{ width: { xs: 80, sm: 100 }, height: { xs: 80, sm: 100 }, bgcolor: '#34495e', border: '4px solid #fff', boxShadow: 2 }}>
                                {getInitials(`${userDetails?.first_name} ${userDetails?.last_name}`)}
                            </Avatar>
                            {/* [PROFILE PIC UPLOAD] camera icon overlay button to trigger file picker */}
                            {/* <IconButton onClick={handleEditClick} sx={{ position: 'absolute', bottom: 0, right: 0, bgcolor: '#fff', boxShadow: 1, width: 30, height: 30 }} size="small"> */}
                            {/*     <PhotoCameraIcon sx={{ fontSize: '1rem' }} /> */}
                            {/* </IconButton> */}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>{userDetails?.first_name} {userDetails?.last_name}</Typography>
                            <Typography variant="body2" color="text.secondary">{userDetails?.designation_name}</Typography>
                            <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: { xs: 'center', sm: 'flex-start' }, flexWrap: 'wrap' }}>
                                {userRole !== 'approver' && <Chip label={`ID: ${userDetails?.emp_id}`} size="small" variant="outlined" sx={{ px: 1, display: 'flex', alignItems: 'center' }} icon={<BusinessCenterIcon sx={{ fontSize: '16px !important' }} />} />}
                                {userRole !== 'approver' && <Chip label={`DOJ: ${formatDate(userDetails?.doj)}`} size="small" variant="outlined" sx={{ px: 1, display: 'flex', alignItems: 'center' }} icon={<EventAvailableIcon sx={{ fontSize: '16px !important' }} />} />}
                                <Chip label={isActive ? "Active" : "Inactive"} size="small" sx={{ bgcolor: isActive ? '#2ecc71' : '#e74c3c', color: '#fff', px: 1 }} />
                            </Box>
                        </Box>
                        <Button fullWidth={isMobile} variant="contained" color="error" startIcon={<LogoutIcon />} onClick={handleLogout} sx={{ mt: { xs: 1, sm: 0 }, ml: { sm: 'auto' } }}>Sign Out</Button>
                    </Box>
                    <MyProfilePage />
                </DialogContent>
            </Dialog>

            {/* Attendance/Check-In Dialog */}
            <Dialog
                open={openCheckInDialog}
                onClose={() => setOpenCheckInDialog(false)}
                maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: { xs: 2, sm: 4 }, margin: { xs: 1, sm: 2 } } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: { xs: 1.5, sm: 2 } }}>
                    {/* Fixed nested typography hydration error */}
                    <Typography variant="h6" component="span" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Welcome Back!
                    </Typography>
                    <IconButton size="small" onClick={() => setOpenCheckInDialog(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', pt: 0, pb: 4 }}>
                    <MyAttendancePunch onStateChange={(status) => setIsCheckedIn(status)} />
                </DialogContent>
            </Dialog>
        </AppBar>
    );
};

export default Navbar;